import type { z } from 'zod'

import { env } from '@/lib/env'

import { requireAuthHeaders, unauthorizedResponseError } from './auth-token'
import { ApiError, ApiParseError } from './errors'

type Json = Record<string, unknown> | unknown[]

interface RequestOptions<TSchema extends z.ZodType> {
  /** Optional service prefix; other callers continue using VITE_API_BASE_URL. */
  baseUrl?: string
  /** Preserve service envelopes when the caller needs to validate success flags. */
  unwrapResponse?: boolean
  /** Schema the response body is validated against before it reaches callers. */
  schema: TSchema
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: Json | FormData
  /** Appended as a query string; empty entries are dropped. */
  searchParams?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  headers?: Record<string, string>
  /** Set on endpoints that must not carry the session token (login, refresh). */
  skipAuth?: boolean
}

interface BlobRequestOptions {
  searchParams?: RequestOptions<z.ZodType>['searchParams']
  signal?: AbortSignal
}

function buildUrl(
  path: string,
  searchParams: RequestOptions<z.ZodType>['searchParams'],
  baseUrl = env.VITE_API_BASE_URL,
) {
  const target = `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`
  // The base may be a proxied path (`/api`), so resolve against the page origin.
  const url = new URL(target, window.location.origin)
  for (const [key, value] of Object.entries(searchParams ?? {})) {
    if (value === undefined || value === null || value === '') continue
    url.searchParams.set(key, String(value))
  }
  return url.toString()
}

/**
 * Envelope the API returns. Adjust if the backend responds with the payload at
 * the top level instead.
 */
function unwrap(payload: unknown): unknown {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: unknown }).data
  }
  return payload
}

function localeText(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value
  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  if (typeof record.id === 'string' && record.id.length > 0) return record.id
  if (typeof record.en === 'string' && record.en.length > 0) return record.en
  return undefined
}

function extractErrorMessage(payload: unknown, fallback: string) {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    const nested =
      localeText(record.message) ??
      localeText(record.error) ??
      localeText(record.detail) ??
      (record.error && typeof record.error === 'object'
        ? localeText((record.error as Record<string, unknown>).message)
        : undefined)
    if (nested) return nested
  }
  return fallback
}

export async function apiRequest<TSchema extends z.ZodType>(
  path: string,
  {
    schema,
    method = 'GET',
    body,
    searchParams,
    signal,
    headers,
    skipAuth,
    baseUrl,
    unwrapResponse = true,
  }: RequestOptions<TSchema>,
): Promise<z.infer<TSchema>> {
  // Throws before the fetch when the session token is gone, so no protected
  // endpoint is ever hit without an Authorization header.
  const authHeaders = skipAuth ? {} : requireAuthHeaders(headers)
  const isFormData = body instanceof FormData

  let response: Response
  try {
    response = await fetch(buildUrl(path, searchParams, baseUrl), {
      method,
      signal,
      headers: {
        Accept: 'application/json',
        ...(body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
        ...authHeaders,
        ...headers,
      },
      ...(body ? { body: isFormData ? body : JSON.stringify(body) } : {}),
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    if (cause instanceof DOMException && cause.name === 'TimeoutError') {
      throw new ApiError('Permintaan terlalu lama. Silakan coba lagi.', 0, 'TIMEOUT', cause)
    }
    // Offline, DNS failure, CORS rejection — status 0 marks "never reached the server".
    throw new ApiError('Koneksi bermasalah. Periksa jaringan kamu.', 0, 'NETWORK_ERROR', cause)
  }

  const raw: unknown = response.status === 204 ? null : await response.json().catch(() => null)

  if (!response.ok) {
    // Session died server-side; let the auth layer clear it and bounce to login.
    if (response.status === 401 && !skipAuth && Object.keys(authHeaders).length > 0) {
      throw unauthorizedResponseError(
        { ...authHeaders, ...headers },
        extractErrorMessage(raw, 'Permintaan tidak diizinkan.'),
      )
    }

    throw new ApiError(
      extractErrorMessage(raw, `Permintaan gagal (${response.status}).`),
      response.status,
      typeof (raw as { code?: unknown })?.code === 'string'
        ? (raw as { code: string }).code
        : undefined,
      raw,
    )
  }

  const result = schema.safeParse(unwrapResponse ? unwrap(raw) : raw)
  if (!result.success) {
    throw new ApiParseError(`Format respons tidak sesuai untuk ${path}.`, result.error.issues)
  }
  return result.data
}

/** Authenticated binary download for endpoints such as generated PDF labels. */
export async function apiBlobRequest(
  path: string,
  { searchParams, signal }: BlobRequestOptions = {},
): Promise<Blob> {
  const authHeaders = requireAuthHeaders()
  let response: Response

  try {
    response = await fetch(buildUrl(path, searchParams), {
      signal,
      headers: {
        Accept: 'application/pdf',
        ...authHeaders,
      },
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError('Koneksi bermasalah. Periksa jaringan kamu.', 0, 'NETWORK_ERROR', cause)
  }

  if (!response.ok) {
    const raw: unknown = await response.json().catch(() => null)
    if (response.status === 401) {
      throw unauthorizedResponseError(
        authHeaders,
        extractErrorMessage(raw, 'Permintaan tidak diizinkan.'),
      )
    }
    throw new ApiError(
      extractErrorMessage(raw, `Permintaan gagal (${response.status}).`),
      response.status,
      typeof (raw as { code?: unknown })?.code === 'string'
        ? (raw as { code: string }).code
        : undefined,
      raw,
    )
  }

  return response.blob()
}
